import { useState } from 'react';
import styled, { css } from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { ExpandingTextArea } from 'foundations-components/transitional/components/ExpandingTextArea';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { HR } from 'foundations-components/transitional/components/HR';
import { StopIcon } from 'foundations-assets/icons/components/StopIcon';
import { TwoFAOutlineIcon } from 'foundations-assets/icons/components/TwoFAOutlineIcon';
import { BreezeButton } from 'ai-components-ui-library/components/buttons/BreezeButton';
import { BreezeSendIcon } from 'ai-components-ui-library/components/icons/internal/BreezeSendIcon';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_LEADING_ICON = /*#__PURE__*/_jsx(TwoFAOutlineIcon, {
  color: "text-core-disabled",
  size: "500"
});
const StyledTextareaOverrides = styled.div.withConfig({
  displayName: "ChatTextInput__StyledTextareaOverrides"
})(["", " inline-size:100%;"], css(["textarea{max-block-size:132px !important;overflow-y:auto !important;}"]));
export const ChatTextInput = ({
  autoFocus,
  disabled = false,
  header,
  inputRef,
  leadingIcon = DEFAULT_LEADING_ICON,
  onChatInputChanged,
  onKeyDown,
  onPrebuiltOptionSelect,
  onSend,
  onStop,
  placeholder,
  prebuiltOptions,
  value
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hoveredOptionValue, setHoveredOptionValue] = useState(null);
  const sendDisabled = disabled || !value || value.trim().length === 0;
  return /*#__PURE__*/_jsx(StyledTextareaOverrides, {
    children: /*#__PURE__*/_jsxs(View, {
      backgroundColor: "fill-surface-default",
      borderColor: isFocused ? 'border-secondary-default' : 'border-core-default',
      borderRadius: prebuiltOptions !== null && prebuiltOptions !== void 0 && prebuiltOptions.length ? '400' : '300',
      borderStyle: "solid",
      borderWidth: "100",
      boxShadow: "100",
      inlineSize: "100%",
      paddingBlock: "400",
      paddingInline: "400",
      position: "relative",
      children: [header && /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        children: header
      }), !!(prebuiltOptions !== null && prebuiltOptions !== void 0 && prebuiltOptions.length) && /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        gap: "200",
        marginBlockEnd: "300",
        children: prebuiltOptions.map(option => /*#__PURE__*/_jsx(Clickable, {
          inlineSize: "100%",
          onClick: () => onPrebuiltOptionSelect === null || onPrebuiltOptionSelect === void 0 ? void 0 : onPrebuiltOptionSelect(option.value),
          onMouseEnter: () => setHoveredOptionValue(option.value),
          onMouseLeave: () => setHoveredOptionValue(null),
          children: /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            backgroundColor: option.selected || hoveredOptionValue === option.value ? 'fill-field-hover' : undefined,
            borderRadius: "400",
            inlineSize: "100%",
            paddingBlock: "100",
            paddingInline: "200",
            children: option.label
          })
        }, option.value))
      }), !!(prebuiltOptions !== null && prebuiltOptions !== void 0 && prebuiltOptions.length) && /*#__PURE__*/_jsx(HR, {
        "data-test-id": "chat-input-separator",
        distance: "flush"
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "end",
        flexDirection: "row",
        inlineSize: "100%",
        justifyContent: "space-between",
        paddingBlockStart: prebuiltOptions !== null && prebuiltOptions !== void 0 && prebuiltOptions.length ? '300' : '0',
        children: [/*#__PURE__*/_jsxs(View, {
          inlineSize: "100%",
          paddingBlockEnd: "0",
          position: "relative",
          children: [leadingIcon && /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            insetBlockStart: "100",
            insetInlineStart: "100",
            justifyContent: "center",
            position: "absolute",
            children: leadingIcon
          }), /*#__PURE__*/_jsx(ExpandingTextArea, {
            autoFocus: autoFocus,
            backgroundColor: "fill-surface-default",
            "data-test-id": "chat-input-element",
            disableFocusStyles: true,
            disabled: disabled,
            id: "chat-input-element",
            inputRef: inputRef,
            maxHeight: 132,
            minBlockSize: "22px",
            onBlur: () => setIsFocused(false),
            onChange: onChatInputChanged,
            onFocus: () => setIsFocused(true),
            onKeyDown: onKeyDown,
            padding: "0",
            paddingInlineStart: leadingIcon ? '700' : '0',
            placeholder: placeholder,
            resize: true,
            shrink: true,
            styled: false,
            value: value
          })]
        }), /*#__PURE__*/_jsx(View, {
          paddingInlineStart: "400",
          children: onStop ? /*#__PURE__*/_jsx(IconButton, {
            "aria-label": I18n.text('academyCommon.chatTextInput.stop'),
            "data-test-id": "chat-stop-button",
            onClick: onStop,
            shape: "circle",
            size: "xs",
            use: "transparent",
            children: /*#__PURE__*/_jsx(StopIcon, {})
          }) : /*#__PURE__*/_jsx(BreezeButton, {
            "aria-label": I18n.text('academyCommon.chatTextInput.send'),
            "data-test-id": "chat-send-button",
            disabled: sendDisabled,
            name: "send",
            onClick: onSend,
            shape: "circle",
            size: "xs",
            children: /*#__PURE__*/_jsx(BreezeSendIcon, {})
          })
        })]
      })]
    })
  });
};